import React, { useState, useEffect, useCallback } from 'react';
import { View, StyleSheet, Dimensions, Image, Text, TextInput, Pressable, ScrollView, Alert, useColorScheme, useWindowDimensions } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { useQuery, useMutation, gql } from '@apollo/client';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { WebView } from 'react-native-webview';
import { Picker } from '@react-native-picker/picker';
import { v4 as uuidv4 } from 'uuid';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { getDatePlaceholder, formatDisplayDate, parseInputDate } from '../utils/dateUtils';
import Config, { buildTaxCategories, buildTaxesFromCategory } from '../constants/Config';

const taxProfile = Config.taxProfile;
const taxCategories = buildTaxCategories(taxProfile);
// Context-aware label: "VAT" for VAT-type profiles, "Tax" otherwise
const taxLabel = taxProfile?.type === 'VAT' ? 'VAT' : 'Tax';

const INVOICE_CONTENT_QUERY = gql`
  query getInvoiceContent($token: String!, $filename: String!, $dateFrom: String!) {
    invoiceContent(token: $token, filename: $filename, dateFrom: $dateFrom)
  }
`;

const INVOICE_METADATA_QUERY = gql`
  query getInvoiceMetadata($token: String!, $filename: String!, $dateFolder: String!) {
    invoiceMetadata(token: $token, filename: $filename, dateFolder: $dateFolder) {
      id
      filename
      dateFolder
      supplierId
      supplier { id name }
      invoiceNumber
      invoiceDate
      dueDate
      totalAmount
      currency
      taxAmount
      notes
      items {
        id
        inventoryItemId
        inventoryItem { id name currentQuantity unit }
        description
        quantity
        unit
        unitPrice
        priceIncludesTax
        discount
        discountType
        totalPrice
        priceBeforeTax
        priceAfterTax
        taxes { type label rate amount category jurisdiction }
        taxCategory
        itemType
      }
    }
  }
`;

const SUPPLIERS_QUERY = gql`
  query getSuppliers($token: String!) {
    suppliers(token: $token) { id name }
  }
`;

const INVENTORY_ITEMS_QUERY = gql`
  query getInventoryItems($token: String!) {
    inventoryItems(token: $token) { id name unit currentQuantity }
  }
`;

const CREATE_SUPPLIER_MUTATION = gql`
  mutation createSupplier($token: String!, $id: ID!, $name: String!) {
    createSupplier(token: $token, id: $id, name: $name) { id name }
  }
`;

const SAVE_INVOICE_METADATA_MUTATION = gql`
  mutation saveInvoiceMetadata(
    $token: String!, $filename: String!, $dateFolder: String!,
    $supplierId: ID, $invoiceNumber: String, $invoiceDate: String,
    $dueDate: String, $totalAmount: Float, $currency: String,
    $taxAmount: Float, $notes: String, $items: [InvoiceMetadataItemInput!]
  ) {
    saveInvoiceMetadata(
      token: $token, filename: $filename, dateFolder: $dateFolder,
      supplierId: $supplierId, invoiceNumber: $invoiceNumber,
      invoiceDate: $invoiceDate, dueDate: $dueDate, totalAmount: $totalAmount,
      currency: $currency, taxAmount: $taxAmount, notes: $notes, items: $items
    ) {
      id filename
      items { id inventoryItemId description quantity unit unitPrice priceIncludesTax discount discountType totalPrice priceBeforeTax priceAfterTax taxes { type label rate amount category jurisdiction } taxCategory itemType }
    }
  }
`;

const PARSE_INVOICE_IMAGE_MUTATION = gql`
  mutation parseInvoiceImage($token: String!, $filename: String!, $dateFolder: String!) {
    parseInvoiceImage(token: $token, filename: $filename, dateFolder: $dateFolder) {
      supplierName
      invoiceNumber
      invoiceDate
      dueDate
      totalAmount
      currency
      taxAmount
      items {
        description
        quantity
        unit
        unitPrice
        discount
        totalPrice
        taxes { type label rate amount category jurisdiction }
        taxCategory
        itemType
      }
    }
  }
`;

// Helper: compute the combined tax rate from a taxes array
function getCombinedRate(taxes) {
  if (!taxes || taxes.length === 0) return 0;
  return taxes.reduce((sum, t) => sum + (parseFloat(t.rate) || 0), 0);
}

// Helper: recalculate tax amounts on each tax component given a net total
function recalcTaxAmounts(taxes, netTotal) {
  if (!taxes) return [];
  return taxes.map(t => ({
    ...t,
    amount: parseFloat((netTotal * (parseFloat(t.rate) || 0) / 100).toFixed(2)),
  }));
}

export default function InvoicePreviewScreen() {
  const route = useRoute();
  const { invoiceFile, dateFrom } = route.params;
  const { width } = useWindowDimensions();
  const isWide = width >= 768;
  const [token, setToken] = useState("");
  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";

  // Form state
  const [metaForm, setMetaForm] = useState({
    supplierId: "", invoiceNumber: "", invoiceDate: "", dueDate: "",
    totalAmount: "", currency: "EUR", taxAmount: "", notes: "",
  });
  const [metaItems, setMetaItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const dateFolder = dateFrom.replace(/-/g, '').substring(0, 6);

  useEffect(() => {
    AsyncStorage.getItem("token").then(t => setToken(t)).catch(() => {});
  }, []);

  const { loading: contentLoading, data: contentData } = useQuery(INVOICE_CONTENT_QUERY, {
    variables: { token, filename: invoiceFile, dateFrom },
    skip: !token,
  });

  const { data: metaData, refetch: refetchMeta } = useQuery(INVOICE_METADATA_QUERY, {
    variables: { token, filename: invoiceFile, dateFolder },
    skip: !token,
  });

  const { data: suppliersData, refetch: refetchSuppliers } = useQuery(SUPPLIERS_QUERY, {
    variables: { token }, skip: !token,
  });

  const { data: inventoryData, refetch: refetchInventory } = useQuery(INVENTORY_ITEMS_QUERY, {
    variables: { token }, skip: !token,
  });

  const [saveInvoiceMetadata] = useMutation(SAVE_INVOICE_METADATA_MUTATION);
  const [createSupplierMutation] = useMutation(CREATE_SUPPLIER_MUTATION);
  const [parseInvoiceImageMutation] = useMutation(PARSE_INVOICE_IMAGE_MUTATION);
  const [showNewSupplier, setShowNewSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState("");
  const [parsing, setParsing] = useState(false);

  const handleCreateSupplier = async () => {
    if (!newSupplierName.trim()) {
      Alert.alert("Error", "Please enter a supplier name");
      return;
    }
    try {
      const result = await createSupplierMutation({
        variables: { token, id: uuidv4(), name: newSupplierName.trim() },
      });
      const newId = result.data?.createSupplier?.id;
      if (newId) {
        setMetaForm(prev => ({ ...prev, supplierId: newId }));
      }
      setNewSupplierName("");
      await refetchSuppliers();
      setShowNewSupplier(false);
    } catch (err) {
      Alert.alert("Error creating supplier", err.message);
    }
  };

  const handleAutofill = async () => {
    setParsing(true);
    try {
      const result = await parseInvoiceImageMutation({
        variables: { token, filename: invoiceFile, dateFolder },
      });
      const parsed = result.data?.parseInvoiceImage;
      if (!parsed) {
        Alert.alert("Auto-fill", "No data could be extracted from this invoice.");
        return;
      }

      // Match supplier name to existing suppliers
      let matchedSupplierId = "";
      if (parsed.supplierName) {
        const match = suppliers.find(s =>
          s.name.toLowerCase() === parsed.supplierName.toLowerCase()
        );
        if (match) {
          matchedSupplierId = match.id;
        }
      }

      // Update header fields (only fill empty fields, don't overwrite existing data)
      setMetaForm(prev => ({
        ...prev,
        supplierId: prev.supplierId || matchedSupplierId,
        invoiceNumber: prev.invoiceNumber || parsed.invoiceNumber || "",
        invoiceDate: prev.invoiceDate || (parsed.invoiceDate ? formatDisplayDate(parsed.invoiceDate) : ""),
        dueDate: prev.dueDate || (parsed.dueDate ? formatDisplayDate(parsed.dueDate) : ""),
        totalAmount: prev.totalAmount || (parsed.totalAmount != null ? String(parsed.totalAmount) : ""),
        currency: prev.currency !== "EUR" ? prev.currency : (parsed.currency || prev.currency),
        taxAmount: prev.taxAmount || (parsed.taxAmount != null ? String(parsed.taxAmount) : ""),
      }));

      // Add parsed line items if no items exist yet
      if (metaItems.length === 0 && parsed.items?.length > 0) {
        const newItems = parsed.items.map(item => {
          const taxes = item.taxes || [];
          const combinedRate = getCombinedRate(taxes);
          const net = item.totalPrice || 0;
          const gross = combinedRate > 0 ? net * (1 + combinedRate / 100) : net;
          return {
            id: uuidv4(),
            inventoryItemId: "",
            description: item.description || "",
            quantity: item.quantity || 1,
            unit: item.unit || "",
            unitPrice: item.unitPrice || 0,
            priceIncludesTax: false,
            discount: item.discount || 0,
            discountType: "percent",
            totalPrice: net,
            priceBeforeTax: net,
            priceAfterTax: gross,
            _manualBeforeTax: false,
            _manualAfterTax: false,
            taxes: recalcTaxAmounts(taxes, net),
            taxCategory: item.taxCategory || "",
            itemType: item.itemType || "product",
          };
        });
        setMetaItems(newItems);
      }

      const warnings = [];
      if (parsed.supplierName && !matchedSupplierId) {
        warnings.push(`Supplier "${parsed.supplierName}" not found in your list. You can create it manually.`);
      }
      if (metaItems.length > 0 && parsed.items?.length > 0) {
        warnings.push("Line items were not replaced because items already exist.");
      }

      Alert.alert(
        "Auto-fill Complete",
        warnings.length > 0
          ? `Invoice data extracted.\n\n${warnings.join("\n")}`
          : "Invoice data extracted and filled in successfully."
      );
    } catch (err) {
      Alert.alert("Auto-fill Error", err.message);
    } finally {
      setParsing(false);
    }
  };

  // Reset form when navigating to a different invoice
  useEffect(() => {
    setMetaForm({
      supplierId: "", invoiceNumber: "", invoiceDate: "", dueDate: "",
      totalAmount: "", currency: "EUR", taxAmount: "", notes: "",
    });
    setMetaItems([]);
    setSaving(false);
    setParsing(false);
  }, [invoiceFile]);

  // Populate form from loaded metadata
  useEffect(() => {
    const meta = metaData?.invoiceMetadata;
    if (meta) {
      setMetaForm({
        supplierId: meta.supplierId || "",
        invoiceNumber: meta.invoiceNumber || "",
        invoiceDate: formatDisplayDate(meta.invoiceDate) || "",
        dueDate: formatDisplayDate(meta.dueDate) || "",
        totalAmount: meta.totalAmount != null ? String(meta.totalAmount) : "",
        currency: meta.currency || "EUR",
        taxAmount: meta.taxAmount != null ? String(meta.taxAmount) : "",
        notes: meta.notes || "",
      });
      setMetaItems(meta.items?.map(i => ({
        id: i.id,
        inventoryItemId: i.inventoryItemId || "",
        description: i.description,
        quantity: i.quantity,
        unit: i.unit || "",
        unitPrice: i.unitPrice,
        priceIncludesTax: i.priceIncludesTax || false,
        discount: i.discount != null ? i.discount : 0,
        discountType: i.discountType || "percent",
        totalPrice: i.totalPrice,
        priceBeforeTax: i.priceBeforeTax,
        priceAfterTax: i.priceAfterTax,
        _manualBeforeTax: false,
        _manualAfterTax: false,
        taxes: i.taxes || [],
        taxCategory: i.taxCategory || "",
        itemType: i.itemType || "product",
      })) || []);
    }
  }, [metaData]);

  // Mime type detection
  const getMimeType = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return 'application/pdf';
    if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
    if (ext === 'png') return 'image/png';
    return 'image/png';
  };
  const mimeType = getMimeType(invoiceFile);

  // Line item helpers
  const addMetaItem = () => {
    setMetaItems([...metaItems, {
      id: uuidv4(), inventoryItemId: "", description: "",
      quantity: 1, unit: "", unitPrice: "", priceIncludesTax: false, discount: 0, discountType: "percent", totalPrice: 0,
      priceBeforeTax: 0, priceAfterTax: 0, _manualBeforeTax: false, _manualAfterTax: false,
      taxes: [], taxCategory: "", itemType: "product",
    }]);
  };

  const updateMetaItem = (index, field, value) => {
    // Functional updater: the Picker fires this twice synchronously (taxCategory then taxes);
    // reading the render-scope metaItems would make the second call clobber the first.
    setMetaItems((prev) => {
    const updated = [...prev];
    updated[index] = { ...updated[index], [field]: value };
    // Mark manual override flags when user directly edits tax totals
    if (field === "priceBeforeTax") {
      updated[index]._manualBeforeTax = true;
    }
    if (field === "priceAfterTax") {
      updated[index]._manualAfterTax = true;
    }
    // Recalculate totals when quantity, price, discount, discountType, priceIncludesTax, or taxes change
    if (["quantity", "unitPrice", "discount", "discountType", "priceIncludesTax", "taxes"].includes(field)) {
      const qty = parseFloat(updated[index].quantity) || 0;
      const price = parseFloat(updated[index].unitPrice) || 0;
      const discountVal = parseFloat(updated[index].discount) || 0;
      const dType = updated[index].discountType || "percent";
      const taxes = updated[index].taxes || [];
      const combinedRate = getCombinedRate(taxes);
      const taxInclusive = updated[index].priceIncludesTax || false;
      const gross = qty * price;
      const afterDiscount = Math.max(0, dType === "flat" ? gross - discountVal : gross * (1 - discountVal / 100));
      // totalPrice is always the net amount (before tax)
      const netTotal = taxInclusive && combinedRate > 0 ? afterDiscount / (1 + combinedRate / 100) : afterDiscount;
      updated[index].totalPrice = netTotal;
      // Recalculate tax component amounts
      updated[index].taxes = recalcTaxAmounts(taxes, netTotal);
      // Only overwrite tax totals if not manually set
      if (!updated[index]._manualBeforeTax) {
        updated[index].priceBeforeTax = netTotal;
      }
      if (!updated[index]._manualAfterTax) {
        updated[index].priceAfterTax = taxInclusive ? afterDiscount : afterDiscount + (netTotal * combinedRate / 100);
      }
    }
    // When selecting an existing inventory item, auto-fill description/unit
    if (field === "inventoryItemId" && value) {
      const inv = (inventoryData?.inventoryItems || []).find(i => i.id === value);
      if (inv) {
        updated[index].description = inv.name;
        updated[index].unit = inv.unit || updated[index].unit;
      }
    }
    return updated;
    });
  };

  const removeMetaItem = (index) => {
    setMetaItems(metaItems.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await saveInvoiceMetadata({
        variables: {
          token, filename: invoiceFile, dateFolder,
          supplierId: metaForm.supplierId || null,
          invoiceNumber: metaForm.invoiceNumber || null,
          invoiceDate: metaForm.invoiceDate ? parseInputDate(metaForm.invoiceDate) : null,
          dueDate: metaForm.dueDate ? parseInputDate(metaForm.dueDate) : null,
          totalAmount: metaForm.totalAmount ? parseFloat(metaForm.totalAmount) : null,
          currency: metaForm.currency || null,
          taxAmount: metaForm.taxAmount ? parseFloat(metaForm.taxAmount) : null,
          notes: metaForm.notes || null,
          items: metaItems.map(i => ({
            id: i.id || uuidv4(),
            inventoryItemId: i.inventoryItemId || null,
            description: i.description,
            quantity: parseFloat(i.quantity) || 0,
            unit: i.unit || null,
            unitPrice: parseFloat(i.unitPrice) || 0,
            priceIncludesTax: i.priceIncludesTax || false,
            discount: i.discount != null ? parseFloat(i.discount) : null,
            discountType: i.discountType || "percent",
            totalPrice: parseFloat(i.totalPrice) || 0,
            priceBeforeTax: i.priceBeforeTax != null ? parseFloat(i.priceBeforeTax) : null,
            priceAfterTax: i.priceAfterTax != null ? parseFloat(i.priceAfterTax) : null,
            taxes: (i.taxes || []).map(t => ({
              type: t.type,
              label: t.label,
              rate: parseFloat(t.rate) || 0,
              amount: parseFloat(t.amount) || 0,
              category: t.category || null,
              jurisdiction: t.jurisdiction || null,
            })),
            taxCategory: i.taxCategory || null,
            itemType: i.itemType || "product",
          })),
        },
      });
      // Update local items with inventory item IDs returned from server
      const savedItems = result.data?.saveInvoiceMetadata?.items || [];
      setMetaItems(savedItems.map(i => ({
        ...i, inventoryItemId: i.inventoryItemId || "", priceIncludesTax: i.priceIncludesTax || false, discountType: i.discountType || "percent", taxCategory: i.taxCategory || "", itemType: i.itemType || "product",
        taxes: i.taxes || [], _manualBeforeTax: false, _manualAfterTax: false,
      })));
      refetchMeta();
      refetchInventory();
      Alert.alert("Saved", "Invoice details saved. Inventory items linked automatically.");
    } catch (err) {
      Alert.alert("Error", err.message);
    } finally {
      setSaving(false);
    }
  };

  const textColor = isDark ? "#fff" : "#000";
  const bg = isDark ? "#121212" : "#f0f0f0";
  const cardBg = isDark ? "#1e1e1e" : "#fff";
  const inputBg = isDark ? "#2a2a2a" : "#f5f5f5";
  const suppliers = suppliersData?.suppliers || [];
  const inventoryItems = inventoryData?.inventoryItems || [];

  // Gesture state for pinch-zoom and pan on images
  const scale = useSharedValue(1);
  const savedScale = useSharedValue(1);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const savedTranslateX = useSharedValue(0);
  const savedTranslateY = useSharedValue(0);

  const pinchGesture = Gesture.Pinch()
    .onUpdate((e) => {
      scale.value = Math.max(1, Math.min(5, savedScale.value * e.scale));
    })
    .onEnd(() => {
      savedScale.value = scale.value;
      if (scale.value <= 1) {
        scale.value = withTiming(1);
        savedScale.value = 1;
        translateX.value = withTiming(0);
        translateY.value = withTiming(0);
        savedTranslateX.value = 0;
        savedTranslateY.value = 0;
      }
    });

  const panGesture = Gesture.Pan()
    .onUpdate((e) => {
      translateX.value = savedTranslateX.value + e.translationX;
      translateY.value = savedTranslateY.value + e.translationY;
    })
    .onEnd(() => {
      savedTranslateX.value = translateX.value;
      savedTranslateY.value = translateY.value;
    });

  const doubleTapGesture = Gesture.Tap()
    .numberOfTaps(2)
    .onEnd(() => {
      if (scale.value > 1) {
        scale.value = withTiming(1);
        savedScale.value = 1;
        translateX.value = withTiming(0);
        translateY.value = withTiming(0);
        savedTranslateX.value = 0;
        savedTranslateY.value = 0;
      } else {
        scale.value = withTiming(2.5);
        savedScale.value = 2.5;
      }
    });

  const composedGesture = Gesture.Simultaneous(pinchGesture, panGesture, doubleTapGesture);

  const animatedImageStyle = useAnimatedStyle(() => ({
    transform: [
      { scale: scale.value },
      { translateX: translateX.value },
      { translateY: translateY.value },
    ],
  }));

  // Invoice viewer panel
  const renderViewer = () => (
    <View style={[styles.viewerPanel, !isWide && { height: 400 }]}>
      {contentLoading && <Text style={styles.loadingText}>Loading invoice...</Text>}
      {contentData?.invoiceContent && (
        mimeType === 'application/pdf' ? (
          <WebView
            source={{
              html: `<html><head><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
                <body style="margin:0;padding:0;overflow:hidden;">
                  <embed src="data:${mimeType};base64,${contentData.invoiceContent}" width="100%" height="100%" type="${mimeType}" />
                </body></html>`
            }}
            style={{ flex: 1 }}
            javaScriptEnabled domStorageEnabled startInLoadingState scalesPageToFit
          />
        ) : (
          <GestureDetector gesture={composedGesture}>
            <Animated.View style={[{ width: '100%', height: '100%' }, animatedImageStyle]}>
              <Image
                source={{ uri: `data:${mimeType};base64,${contentData.invoiceContent}` }}
                style={{ width: '100%', height: '100%' }}
                resizeMode="contain"
              />
            </Animated.View>
          </GestureDetector>
        )
      )}
      {!contentLoading && !contentData?.invoiceContent && (
        <Text style={styles.loadingText}>No preview available</Text>
      )}
    </View>
  );

  // Details panel
  const renderDetails = () => (
    <ScrollView style={[styles.detailsPanel, { backgroundColor: bg }]} contentContainerStyle={{ padding: 16 }}>
      <Text style={[styles.sectionTitle, { color: textColor }]}>Invoice Details</Text>
      <Text style={[styles.filenameText, { color: "#888" }]}>{invoiceFile}</Text>

      <Pressable
        style={[styles.autofillButton, { opacity: parsing ? 0.6 : 1 }]}
        onPress={handleAutofill}
        disabled={parsing}
      >
        <Text style={styles.autofillButtonText}>{parsing ? "Parsing invoice..." : "Auto-fill with AI"}</Text>
      </Pressable>

      <View style={[styles.pickerContainer, { backgroundColor: inputBg }]}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingRight: 8 }}>
          <Text style={styles.label}>Supplier</Text>
          <Pressable onPress={() => setShowNewSupplier(!showNewSupplier)}>
            <Text style={{ color: '#007AFF', fontSize: 12, fontWeight: '600' }}>
              {showNewSupplier ? 'Cancel' : '+ New'}
            </Text>
          </Pressable>
        </View>
        {showNewSupplier ? (
          <View style={{ flexDirection: 'row', padding: 8, gap: 8 }}>
            <TextInput
              style={[styles.input, { flex: 1, backgroundColor: isDark ? '#333' : '#fff', color: textColor, marginBottom: 0 }]}
              placeholder="Supplier name" placeholderTextColor="#999"
              value={newSupplierName}
              onChangeText={setNewSupplierName}
              autoFocus
            />
            <Pressable
              style={{ backgroundColor: '#28a745', paddingHorizontal: 14, borderRadius: 8, justifyContent: 'center' }}
              onPress={handleCreateSupplier}
              accessibilityRole="button"
            >
              <Text style={{ color: '#fff', fontWeight: '600' }}>Add</Text>
            </Pressable>
          </View>
        ) : (
          <Picker
            selectedValue={metaForm.supplierId}
            onValueChange={(v) => setMetaForm(prev => ({ ...prev, supplierId: v }))}
            style={{ color: textColor }}
          >
            <Picker.Item label="— None —" value="" />
            {suppliers.map(s => <Picker.Item key={s.id} label={s.name} value={s.id} />)}
          </Picker>
        )}
      </View>

      <TextInput style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
        placeholder="Invoice Number" placeholderTextColor="#999"
        value={metaForm.invoiceNumber}
        onChangeText={(v) => setMetaForm({ ...metaForm, invoiceNumber: v })}
      />
      <TextInput style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
        placeholder={`Invoice Date (${getDatePlaceholder()})`} placeholderTextColor="#999"
        value={metaForm.invoiceDate}
        onChangeText={(v) => setMetaForm({ ...metaForm, invoiceDate: v })}
        onBlur={() => {
          if (metaForm.invoiceDate) {
            const iso = parseInputDate(metaForm.invoiceDate);
            if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
              setMetaForm(prev => ({ ...prev, invoiceDate: formatDisplayDate(iso) }));
            }
          }
        }}
      />
      <TextInput style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
        placeholder={`Due Date (${getDatePlaceholder()})`} placeholderTextColor="#999"
        value={metaForm.dueDate}
        onChangeText={(v) => setMetaForm({ ...metaForm, dueDate: v })}
        onBlur={() => {
          if (metaForm.dueDate) {
            const iso = parseInputDate(metaForm.dueDate);
            if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
              setMetaForm(prev => ({ ...prev, dueDate: formatDisplayDate(iso) }));
            }
          }
        }}
      />
      <View style={styles.row}>
        <TextInput style={[styles.input, { flex: 2, backgroundColor: inputBg, color: textColor }]}
          placeholder="Total Amount" placeholderTextColor="#999"
          value={metaForm.totalAmount}
          onChangeText={(v) => setMetaForm({ ...metaForm, totalAmount: v })}
          keyboardType="numeric"
        />
        <View style={[styles.pickerContainer, { flex: 1, marginLeft: 8, backgroundColor: inputBg, marginBottom: 10 }]}>
          <Picker
            selectedValue={metaForm.currency}
            onValueChange={(v) => setMetaForm({ ...metaForm, currency: v })}
            style={{ color: textColor }}
          >
            <Picker.Item label="EUR" value="EUR" />
            <Picker.Item label="USD" value="USD" />
            <Picker.Item label="BGN" value="BGN" />
            <Picker.Item label="GBP" value="GBP" />
          </Picker>
        </View>
      </View>
      <TextInput style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
        placeholder="Tax Amount" placeholderTextColor="#999"
        value={metaForm.taxAmount}
        onChangeText={(v) => setMetaForm({ ...metaForm, taxAmount: v })}
        keyboardType="numeric"
      />
      <TextInput style={[styles.input, { backgroundColor: inputBg, color: textColor, minHeight: 60 }]}
        placeholder="Notes" placeholderTextColor="#999"
        value={metaForm.notes}
        onChangeText={(v) => setMetaForm({ ...metaForm, notes: v })}
        multiline
      />

      {/* Line Items */}
      <View style={styles.lineItemsHeader}>
        <Text style={[styles.sectionTitle, { color: textColor, marginBottom: 0 }]}>Line Items</Text>
      </View>

      {metaItems.map((item, idx) => (
        <View key={item.id || idx} style={[styles.lineItem, { backgroundColor: cardBg }]}>
          {/* Inventory item picker */}
          <View style={[styles.pickerContainer, { backgroundColor: inputBg, marginBottom: 8 }]}>
            <Text style={styles.label}>Inventory Item</Text>
            <Picker
              selectedValue={item.inventoryItemId || ""}
              onValueChange={(v) => updateMetaItem(idx, "inventoryItemId", v)}
              style={{ color: textColor }}
            >
              <Picker.Item label="— New item (auto-create) —" value="" />
              {inventoryItems.map(inv => (
                <Picker.Item key={inv.id} label={`${inv.name}${inv.unit ? ` (${inv.unit})` : ''}`} value={inv.id} />
              ))}
            </Picker>
          </View>
          <TextInput style={[styles.input, { backgroundColor: inputBg, color: textColor }]}
            placeholder="Description / Name" placeholderTextColor="#999"
            value={item.description}
            onChangeText={(v) => updateMetaItem(idx, "description", v)}
          />
          <View style={styles.row}>
            <TextInput style={[styles.input, { flex: 1, backgroundColor: inputBg, color: textColor }]}
              placeholder="Qty" placeholderTextColor="#999"
              value={String(item.quantity)}
              onChangeText={(v) => updateMetaItem(idx, "quantity", v)}
              keyboardType="numeric"
            />
            <View style={[styles.pickerContainer, { flex: 2, marginLeft: 8, backgroundColor: inputBg, marginBottom: 10 }]}>
              <Picker
                selectedValue={item.unit || ""}
                onValueChange={(v) => updateMetaItem(idx, "unit", v)}
                style={{ color: textColor }}
              >
                <Picker.Item label="Unit" value="" />
                <Picker.Item label="pcs" value="pcs" />
                <Picker.Item label="kg" value="kg" />
                <Picker.Item label="g" value="g" />
                <Picker.Item label="L" value="L" />
                <Picker.Item label="mL" value="mL" />
                <Picker.Item label="m" value="m" />
                <Picker.Item label="box" value="box" />
                <Picker.Item label="pack" value="pack" />
                <Picker.Item label="bottle" value="bottle" />
                <Picker.Item label="hr" value="hr" />
              </Picker>
            </View>
          </View>
          <View style={styles.row}>
            <TextInput style={[styles.input, { flex: 1, backgroundColor: inputBg, color: textColor }]}
              placeholder="Unit Price" placeholderTextColor="#999"
              value={item.unitPrice !== "" && item.unitPrice != null ? String(item.unitPrice) : ""}
              onChangeText={(v) => updateMetaItem(idx, "unitPrice", v)}
              keyboardType="numeric"
            />
            <Pressable
              style={[styles.taxInclusiveToggle, { backgroundColor: item.priceIncludesTax ? '#007AFF' : inputBg }]}
              onPress={() => updateMetaItem(idx, "priceIncludesTax", !item.priceIncludesTax)}
            >
              <Text style={{ color: item.priceIncludesTax ? '#fff' : '#888', fontWeight: '600', fontSize: 10 }}>
                {item.priceIncludesTax ? `+${taxLabel}` : "ex."}
              </Text>
            </Pressable>
          </View>
          {/* Discount & Tax fields */}
          <View style={styles.row}>
            <View style={[{ flex: 1, flexDirection: 'row', alignItems: 'center' }]}>
              <TextInput style={[styles.input, { flex: 1, backgroundColor: inputBg, color: textColor, marginBottom: 10 }]}
                placeholder={item.discountType === "flat" ? "Discount" : "Disc %"}
                placeholderTextColor="#999"
                value={item.discount ? String(item.discount) : ""}
                onChangeText={(v) => updateMetaItem(idx, "discount", v)}
                keyboardType="numeric"
              />
              <Pressable
                style={[styles.discountTypeToggle, { backgroundColor: inputBg, marginBottom: 10 }]}
                onPress={() => updateMetaItem(idx, "discountType", item.discountType === "flat" ? "percent" : "flat")}
              >
                <Text style={{ color: textColor, fontWeight: '600', fontSize: 14 }}>
                  {item.discountType === "flat" ? "$" : "%"}
                </Text>
              </Pressable>
            </View>
            <View style={[styles.pickerContainer, { flex: 2, marginLeft: 8, backgroundColor: inputBg, marginBottom: 10 }]}>
              <Picker
                selectedValue={item.taxCategory || ""}
                onValueChange={(v) => {
                  updateMetaItem(idx, "taxCategory", v);
                  // When selecting a tax category, build the taxes array from the profile
                  const newTaxes = buildTaxesFromCategory(taxProfile, v);
                  if (newTaxes.length > 0) {
                    updateMetaItem(idx, "taxes", newTaxes);
                  }
                }}
                style={{ color: textColor }}
              >
                <Picker.Item label={`${taxLabel} Category`} value="" />
                {taxCategories.map(c => (
                  <Picker.Item key={c.value} label={c.label} value={c.value} />
                ))}
              </Picker>
            </View>
          </View>
          {/* Tax components breakdown */}
          {item.taxes && item.taxes.length > 0 && (
            <View style={{ marginBottom: 6 }}>
              {item.taxes.map((tax, tidx) => (
                <View key={tidx} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 4, marginBottom: 2 }}>
                  <Text style={{ color: "#888", fontSize: 11 }}>{tax.label} ({tax.rate}%)</Text>
                  <Text style={{ color: "#888", fontSize: 11 }}>{(parseFloat(tax.amount) || 0).toFixed(2)}</Text>
                </View>
              ))}
            </View>
          )}
          {/* Prices before/after tax — editable for manual override */}
          <View style={styles.row}>
            <View style={{ flex: 1 }}>
              <Text style={{ color: "#888", fontSize: 11, marginBottom: 2 }}>Before {taxLabel}</Text>
              <TextInput style={[styles.input, { backgroundColor: inputBg, color: textColor, fontSize: 14, fontWeight: "500" }]}
                placeholder="0.00" placeholderTextColor="#999"
                value={item.priceBeforeTax != null ? String(item.priceBeforeTax) : ""}
                onChangeText={(v) => updateMetaItem(idx, "priceBeforeTax", v)}
                keyboardType="numeric"
              />
            </View>
            <View style={{ flex: 1, marginLeft: 8 }}>
              <Text style={{ color: "#888", fontSize: 11, marginBottom: 2 }}>After {taxLabel}</Text>
              <TextInput style={[styles.input, { backgroundColor: inputBg, color: textColor, fontSize: 14, fontWeight: "500" }]}
                placeholder="0.00" placeholderTextColor="#999"
                value={item.priceAfterTax != null ? String(item.priceAfterTax) : ""}
                onChangeText={(v) => updateMetaItem(idx, "priceAfterTax", v)}
                keyboardType="numeric"
              />
            </View>
          </View>
          {/* Product / Service toggle */}
          <View style={[styles.row, { marginTop: 8, alignItems: 'center' }]}>
            <Pressable
              style={[styles.typeToggle, item.itemType !== 'service' && styles.typeToggleActive]}
              onPress={() => updateMetaItem(idx, "itemType", "product")}
            >
              <Text style={[styles.typeToggleText, item.itemType !== 'service' && styles.typeToggleTextActive]}>Product</Text>
            </Pressable>
            <Pressable
              style={[styles.typeToggle, { marginLeft: 6 }, item.itemType === 'service' && styles.typeToggleActive]}
              onPress={() => updateMetaItem(idx, "itemType", "service")}
            >
              <Text style={[styles.typeToggleText, item.itemType === 'service' && styles.typeToggleTextActive]}>Service</Text>
            </Pressable>
          </View>
          <View style={styles.lineItemFooter}>
            <Text style={{ color: "#888" }}>Total: {(parseFloat(item.totalPrice) || 0).toFixed(2)}</Text>
            <Text style={{ color: item.itemType === 'service' ? '#6f42c1' : '#17a2b8', fontSize: 12, fontWeight: '600' }}>
              {item.itemType === 'service' ? '◆ Service' : '■ Product'}
            </Text>
            {item.inventoryItemId ? (
              <Text style={{ color: "#28a745", fontSize: 12 }}>✓ Linked</Text>
            ) : (
              <Text style={{ color: "#ff9800", fontSize: 12 }}>Will create new</Text>
            )}
            <Pressable onPress={() => removeMetaItem(idx)}>
              <Text style={{ color: "#dc3545", fontWeight: "600" }}>Remove</Text>
            </Pressable>
          </View>
        </View>
      ))}

      <Pressable style={styles.addItemBtn} onPress={addMetaItem}>
        <Text style={styles.addItemBtnText}>+ Add Item</Text>
      </Pressable>

      {metaItems.length > 0 && (
        <View style={[styles.lineItemsTotalRow, { backgroundColor: cardBg }]}>
          <View style={{ flex: 1 }}>
            <Text style={{ color: "#888", fontSize: 12 }}>Items Total (net)</Text>
            <Text style={{ color: textColor, fontSize: 16, fontWeight: "600" }}>
              {metaItems.reduce((sum, i) => sum + (parseFloat(i.priceBeforeTax) || 0), 0).toFixed(2)}
            </Text>
          </View>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={{ color: "#888", fontSize: 12 }}>Items Total (with {taxLabel})</Text>
            <Text style={{ color: textColor, fontSize: 16, fontWeight: "600" }}>
              {metaItems.reduce((sum, i) => sum + (parseFloat(i.priceAfterTax) || 0), 0).toFixed(2)}
            </Text>
          </View>
          {metaForm.totalAmount ? (
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={{ color: "#888", fontSize: 12 }}>Invoice Total</Text>
              <Text style={{ color: textColor, fontSize: 16, fontWeight: "600" }}>
                {parseFloat(metaForm.totalAmount).toFixed(2)}
              </Text>
            </View>
          ) : null}
        </View>
      )}

      <Pressable
        style={[styles.saveButton, { opacity: saving ? 0.6 : 1 }]}
        onPress={handleSave}
        disabled={saving}
      >
        <Text style={styles.saveButtonText}>{saving ? "Saving..." : "Save Details"}</Text>
      </Pressable>

      <View style={{ height: 40 }} />
    </ScrollView>
  );

  if (isWide) {
    return (
      <View style={[styles.container, { backgroundColor: bg }]}>
        <View style={styles.splitContainer}>
          {renderViewer()}
          {renderDetails()}
        </View>
      </View>
    );
  }

  // Narrow: stack vertically
  return (
    <ScrollView style={[styles.container, { backgroundColor: bg }]}>
      {renderViewer()}
      {renderDetails()}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  splitContainer: { flex: 1, flexDirection: 'row' },
  viewerPanel: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  detailsPanel: { flex: 1 },
  loadingText: { color: '#aaa', fontSize: 16, textAlign: 'center', marginTop: 40 },
  sectionTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 12 },
  filenameText: { fontSize: 13, marginBottom: 16 },
  label: { fontSize: 12, color: '#999', paddingHorizontal: 12, paddingTop: 6 },
  input: { padding: 10, borderRadius: 8, marginBottom: 10, fontSize: 14 },
  row: { flexDirection: 'row' },
  pickerContainer: { borderRadius: 8, marginBottom: 10, padding: 4 },
  lineItemsHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    marginTop: 16, marginBottom: 10,
  },
  addItemBtn: { backgroundColor: '#007AFF', paddingVertical: 10, borderRadius: 8, alignItems: 'center', marginBottom: 12 },
  addItemBtnText: { color: '#fff', fontWeight: '600', fontSize: 14 },
  lineItemsTotalRow: {
    flexDirection: 'row', padding: 12, borderRadius: 8, marginBottom: 12,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 2, elevation: 1,
  },
  lineItem: {
    padding: 12, borderRadius: 8, marginBottom: 10,
    shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 2, elevation: 1,
  },
  lineItemFooter: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 6,
  },
  saveButton: {
    backgroundColor: '#28a745', padding: 14, borderRadius: 8, alignItems: 'center', marginTop: 16,
  },
  saveButtonText: { color: '#fff', fontWeight: 'bold', fontSize: 16 },
  autofillButton: {
    backgroundColor: '#6f42c1', padding: 12, borderRadius: 8, alignItems: 'center', marginBottom: 16,
  },
  autofillButtonText: { color: '#fff', fontWeight: '600', fontSize: 14 },
  taxInclusiveToggle: {
    width: 36, height: 36, borderRadius: 8, marginLeft: 4,
    justifyContent: 'center', alignItems: 'center',
  },
  discountTypeToggle: {
    width: 32, height: 36, borderRadius: 8, marginLeft: 4,
    justifyContent: 'center', alignItems: 'center',
  },
  typeToggle: {
    paddingHorizontal: 12, paddingVertical: 5, borderRadius: 6,
    borderWidth: 1, borderColor: '#666',
  },
  typeToggleActive: {
    backgroundColor: '#007AFF', borderColor: '#007AFF',
  },
  typeToggleText: { fontSize: 12, color: '#888' },
  typeToggleTextActive: { color: '#fff', fontWeight: '600' },
});
